import { connect, ConnectedProps } from 'react-redux';
import { ReduxState } from '../../../interfaces';
import { Button, Input, Modal } from '../../components';
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Divider, ModalProps } from 'antd';
import { authToken } from '../../../scripts';
import { forgotPassword, setNewPassword, verifyForgetPassword } from '../../../services';
import { LoadingOutlined } from '@ant-design/icons';
import OtpInput from '../../components/otpInput/otpInput.index';
import { toast } from 'react-toastify';

interface OwnProps {
    setOpen: Dispatch<SetStateAction<boolean>>;
}

type Props = ModalProps & OwnProps & ConnectedProps<typeof connector>;

enum Stage {
    ForgotPassword,
    VerifyForgetPassword,
    SetNewPassword,
}

const PasswordModal: FC<Props> = (props) => {
    const [stage, setStage] = useState<Stage>(Stage.ForgotPassword);
    const [loading, setLoading] = useState<boolean>(false);
    const [remainingTime, setRemainingTime] = useState<number>(120);
    const [isTimerActive, setIsTimerActive] = useState<boolean>(true);
    const [resendCodeLoading, setResendCodeLoading] = useState<boolean>(false);
    const [code, setCode] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [repeatPassword, setRepeatPassword] = useState<string>('');

    const onForgotPasswordClick = () => {
        const onComplete = () => {
            setStage(Stage.VerifyForgetPassword);
            setRemainingTime(120);
        };

        forgotPassword(setLoading, { phoneNumber: props.user?.phoneNumber }, onComplete);
    };

    const handleResendClick = () => {
        const onComplete = () => {
            setCode('');
            setRemainingTime(120);
            setIsTimerActive(true);
        };

        forgotPassword(setResendCodeLoading, { phoneNumber: props?.user?.phoneNumber }, onComplete);
    };

    const onVerifyForgetPasswordClick = () => {
        const onComplete = (accessToken: string) => {
            const currentToken = authToken.get();

            if (accessToken && currentToken) {
                setStage(Stage.SetNewPassword);
                authToken.set({ ...currentToken, accessToken: accessToken });
            }
        };

        verifyForgetPassword(setLoading, { phoneNumber: props?.user?.phoneNumber, code: code }, onComplete);
    };

    const onSetNewPassword = () => {
        const onComplete = () => {
            toast.success('تغییر رمز عبور با موفقیت انجام شد.');
            props.setOpen(false);
        };

        if (password === repeatPassword) {
            setNewPassword(setLoading, { password, repeatPassword }, onComplete);
        } else {
            toast.error('رمز عبور های وارد شده یکسان نیستند.');
        }
    };

    const renderTitle = () => {
        switch (stage) {
            case Stage.ForgotPassword:
                return 'تغییر رمز عبور';
            case Stage.VerifyForgetPassword:
                return 'تایید کد پیامک شده';
            case Stage.SetNewPassword:
                return 'تعیین رمز عبور جدید';
        }
    };

    const renderStage = () => {
        switch (stage) {
            case Stage.ForgotPassword:
                return (
                    <>
                        <Divider />
                        <p className={'text-xl font-normal text-center'}>
                            کد به شماره {props.user?.phoneNumber} ارسال خواهد شد.
                        </p>
                        <div className={'flex justify-between gap-4 mt-6'}>
                            <Button
                                type={'primary'}
                                className={'w-full'}
                                loading={loading}
                                disabled={loading}
                                onClick={onForgotPasswordClick}
                            >
                                دریافت کد تایید
                            </Button>
                            <Button
                                type={'text'}
                                className={'bg-background w-full'}
                                onClick={() => props.setOpen(false)}
                            >
                                انصراف
                            </Button>
                        </div>
                    </>
                );
            case Stage.VerifyForgetPassword:
                return (
                    <>
                        <Divider />
                        <p className={'flex font-normal text-sm'}>
                            کد پیامک شده به شماره {props.user?.phoneNumber} را وارد کنید.
                        </p>
                        <div className={'flex items-center justify-between mt-4 mb-2'}>
                            <span className={'text-base font-bold'}>کد پیامک شده:</span>
                            {remainingTime === 0 ? (
                                <p
                                    className={`flex text-base items-center gap-2 font-bold clickable ${resendCodeLoading ? '!opacity-50' : ''}`}
                                    onClick={handleResendClick}
                                >
                                    ارسال مجدد کد
                                    {resendCodeLoading ? <LoadingOutlined /> : null}
                                </p>
                            ) : (
                                <p className={'font-medium'}>
                                    <span className={'font-bold'}>{remainingTime} ثانیه </span>تا ارسال مجدد کد
                                </p>
                            )}
                        </div>
                        <OtpInput length={6} onChange={(otp) => setCode(otp)} />
                        <Button
                            type={'primary'}
                            className={'mt-6 w-full'}
                            loading={loading}
                            disabled={loading || code?.length < 6}
                            onClick={onVerifyForgetPasswordClick}
                        >
                            تایید کد
                        </Button>
                    </>
                );
            case Stage.SetNewPassword:
                return (
                    <>
                        <p className={'text-base text-darkPurple-100 mt-6 font-light max-lg:text-sm'}>
                            رمز عبور جدید خود را تنظیم نمایید.
                        </p>
                        <Input
                            type={'password'}
                            className={'mt-6'}
                            placeholder={'رمز عبور'}
                            value={password}
                            dir={password ? 'ltr' : 'rtl'}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <Input
                            type={'password'}
                            className={'mt-4'}
                            placeholder={'تکرار رمز عبور'}
                            value={repeatPassword}
                            dir={repeatPassword ? 'ltr' : 'rtl'}
                            onChange={(e) => setRepeatPassword(e.target.value)}
                        />
                        <ul className={'mt-4 text-xs font-medium flex flex-col gap-1'}>
                            <li className={'flex items-center gap-2'}>
                                <div className={'w-1 h-1 rounded-full bg-black-100'} />
                                رمز عبور با ۸ کارکتر یا بیشتر باشد.
                            </li>
                            <li className={'flex items-center gap-2'}>
                                <div className={'w-1 h-1 rounded-full bg-black-100'} />
                                استفاده از حروف بزرگ
                            </li>
                            <li className={'flex items-center gap-2'}>
                                <div className={'w-1 h-1 rounded-full bg-black-100'} />
                                استفاده از عدد
                            </li>
                            <li className={'flex items-center gap-2'}>
                                <div className={'w-1 h-1 rounded-full bg-black-100'} />
                                استفاده از کارکتر‌های خاص : &، % ، $ ، @
                            </li>
                        </ul>
                        <Button
                            className={'mt-6 w-full'}
                            type={'primary'}
                            loading={loading}
                            onClick={onSetNewPassword}
                            disabled={loading || !password || !repeatPassword}
                        >
                            تایید
                        </Button>
                    </>
                );
        }
    };

    useEffect(() => {
        if (isTimerActive && remainingTime > 0) {
            const timer = setInterval(() => {
                setRemainingTime((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        } else if (remainingTime === 0) {
            setIsTimerActive(false);
        }
    }, [isTimerActive, remainingTime]);

    useEffect(() => {
        if (!props.open) {
            setStage(Stage.ForgotPassword);
            setPassword('');
            setRepeatPassword('');
        }
    }, [props.open]);

    return (
        <Modal open={props.open} setOpen={props.setOpen} title={renderTitle()}>
            {renderStage()}
        </Modal>
    );
};

const mapStateToProps = (state: ReduxState) => ({
    isAuth: state.authStatus,
    user: state.userData,
});

const connector = connect(mapStateToProps);
export default connector(PasswordModal);
